import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  Search,
  ArrowLeft,
  ArrowUpDown,
  AlertOctagon,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import PageMeta from '../../shared/PageMeta';
import {
  dashboardService,
  type SuperAdminBucket,
  type SuperAdminPortfolio,
  type SuperAdminPortfolioProject
} from '../../services/dashboardService';

const BUCKET_LABEL: Record<SuperAdminBucket, string> = {
  completed: 'Completed',
  readyToStart: 'Ready to Start',
  onTrack: 'On Track',
  atRisk: 'At Risk',
  draftIncomplete: 'Draft / Incomplete'
};

const BUCKET_PILL: Record<SuperAdminBucket, string> = {
  completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20',
  readyToStart: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-blue-500/20',
  onTrack: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 ring-teal-500/20',
  atRisk: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20',
  draftIncomplete: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 ring-slate-500/20'
};

const ALL_BUCKETS: SuperAdminBucket[] = [
  'completed', 'readyToStart', 'onTrack', 'atRisk', 'draftIncomplete'
];

type SortKey = 'health' | 'compliance' | 'pending' | 'progress' | 'name';

export default function SuperAdminPortfolio() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBucket = searchParams.get('bucket') as SuperAdminBucket | null;
  const [bucket, setBucket] = useState<SuperAdminBucket | null>(initialBucket);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<SuperAdminPortfolio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'health', dir: 'asc' });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dashboardService.getSuperAdminPortfolio({
        ...(bucket ? { bucket } : {}),
        ...(search ? { search } : {})
      });
      setData(res);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load portfolio';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (bucket) setSearchParams({ bucket });
    else setSearchParams({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const sorted = useMemo(() => {
    const list = [...(data?.projects ?? [])];
    list.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (sort.key) {
        case 'health': av = a.healthScore; bv = b.healthScore; break;
        case 'compliance': av = a.complianceLast7d; bv = b.complianceLast7d; break;
        case 'pending': av = a.pendingTasks; bv = b.pendingTasks; break;
        case 'progress': av = a.progress; bv = b.progress; break;
        case 'name': av = a.projectName; bv = b.projectName; break;
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sort.dir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return list;
  }, [data, sort]);

  const toggleSort = (key: SortKey) => {
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  return (
    <>
      <PageMeta title="Project Portfolio" description="Filterable SuperAdmin project grid" />

      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => navigate('/dashboard/super-admin')}
            className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-violet-600 dark:text-slate-300 dark:hover:text-violet-400"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to overview
          </button>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Project Portfolio</h1>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search project, code, customer…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:ring-violet-900/40"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setBucket(null)}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                !bucket
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700/40 dark:text-slate-300'
              }`}
            >
              All ({data?.total ?? 0})
            </button>
            {ALL_BUCKETS.map(b => (
              <button
                key={b}
                onClick={() => setBucket(b)}
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ring-1 transition ${
                  bucket === b
                    ? `${BUCKET_PILL[b]} ring-2`
                    : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700'
                }`}
              >
                {BUCKET_LABEL[b]} {data ? `(${data.byBucket[b]})` : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Projects table */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Sparkles className="h-6 w-6 animate-pulse text-violet-500" />
          </div>
        ) : error ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 text-rose-600">
            <AlertOctagon className="h-6 w-6" />
            {error}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400 dark:border-slate-700">
            No projects match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
            <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-700">
              <thead className="bg-slate-50/60 text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                <tr>
                  <Th label="Project" onClick={() => toggleSort('name')} sortKey="name" current={sort} />
                  <Th label="Customer" />
                  <Th label="Manager" />
                  <Th label="Bucket" />
                  <Th label="Progress" onClick={() => toggleSort('progress')} sortKey="progress" current={sort} />
                  <Th label="Compliance 7d" onClick={() => toggleSort('compliance')} sortKey="compliance" current={sort} />
                  <Th label="Pending" onClick={() => toggleSort('pending')} sortKey="pending" current={sort} />
                  <Th label="Health" onClick={() => toggleSort('health')} sortKey="health" current={sort} />
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-700/40">
                {sorted.map((p, i) => <Row key={p.projectId} project={p} index={i} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Th({
  label, onClick, sortKey, current
}: {
  label: string;
  onClick?: () => void;
  sortKey?: SortKey;
  current?: { key: SortKey; dir: 'asc' | 'desc' };
}) {
  const active = sortKey && current?.key === sortKey;
  return (
    <th className="px-3 py-2.5 text-left text-[10px] font-semibold">
      {onClick ? (
        <button onClick={onClick} className={`flex items-center gap-1 transition hover:text-violet-600 ${active ? 'text-violet-600 dark:text-violet-400' : ''}`}>
          {label}
          <ArrowUpDown className="h-3 w-3" />
        </button>
      ) : label}
    </th>
  );
}

function Row({ project: p, index: i }: { project: SuperAdminPortfolioProject; index: number }) {
  const navigate = useNavigate();
  const healthClass =
    p.healthScore >= 75 ? 'text-emerald-600 dark:text-emerald-400'
    : p.healthScore >= 50 ? 'text-teal-600 dark:text-teal-400'
    : p.healthScore >= 30 ? 'text-amber-600 dark:text-amber-400'
    : 'text-rose-600 dark:text-rose-400';

  return (
    <motion.tr
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.02 }}
      onClick={() => navigate(`/dashboard/super-admin/projects/${p.projectId}`)}
      className="group cursor-pointer transition hover:bg-violet-50/40 dark:hover:bg-violet-900/20"
    >
      <td className="px-3 py-2.5">
        <p className="font-semibold text-slate-900 dark:text-white">{p.projectName}</p>
        <p className="text-[10px] text-slate-500 dark:text-slate-400">{p.code}</p>
      </td>
      <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{p.customer || '—'}</td>
      <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{p.manager}</td>
      <td className="px-3 py-2.5">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${BUCKET_PILL[p.bucket]}`}>
          {BUCKET_LABEL[p.bucket]}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
            <div className="h-full rounded-full bg-violet-500" style={{ width: `${p.progress}%` }} />
          </div>
          <span className="text-[11px] tabular-nums text-slate-600 dark:text-slate-400">{p.progress}%</span>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <span className={`text-xs font-bold tabular-nums ${
          p.complianceLast7d >= 85 ? 'text-emerald-600 dark:text-emerald-400'
          : p.complianceLast7d >= 60 ? 'text-amber-600 dark:text-amber-400'
          : 'text-rose-600 dark:text-rose-400'
        }`}>
          {p.complianceLast7d}%
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs tabular-nums text-slate-700 dark:text-slate-300">
        {p.pendingTasks}
        {p.overdueTasks > 0 && <span className="ml-1 text-[10px] text-rose-600">+{p.overdueTasks} overdue</span>}
      </td>
      <td className={`px-3 py-2.5 text-base font-bold tabular-nums ${healthClass}`}>{p.healthScore}</td>
      <td className="px-3 py-2.5 text-right">
        <ChevronRight className="ml-auto h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-violet-500" />
      </td>
    </motion.tr>
  );
}
